import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Box,
  Alert,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getDevices } from "../api/devices";
import TopNavBar from "../components/TopNavBar";
import { useAuth } from "../context/AuthContext";
import DeviceCard from "../components/DeviceCard";
import DeviceCarousel from "../components/DeviceCarousel";
import useLanguage from "../hooks/useLanguage";
import { translateControl } from "../assets/variables/controlTranslations";

function DeviceListPage() {
  const { user } = useAuth();
  const username = user?.username;
  // Same language as the controls page / top bar switch
  const [language] = useLanguage();
  const t = (key) => translateControl(language, key);
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch devices with useCallback to avoid recreating the function on each render
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDevices();
      setDevices(data);
      console.log("Fetched devices:", data);
    } catch (err) {
      console.error("Error loading devices:", err.message);
      setError("loadFailed");
    } finally {
      setLoading(false);
    }
  }, []);

  // Only run once on mount
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleDeviceSelect = (deviceSerial) => {
    navigate(`/device-controls/${deviceSerial}`);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "online":
      case "active":
      case "charging":
        return "success";
      case "cleaning":
        return "primary";
      case "offline":
      case "error":
        return "error";
      case "stopped":
      case "paused":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusText = (device) => {
    // You can customize this based on your device data structure
    return device.status || device.state || "Unknown";
  };

  return (
    <>
      <TopNavBar
        title={t("title")}
        username={username}
        heading={t("title")}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {t("deviceList.heading")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("deviceList.subtitle")}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t(`deviceList.${error}`)}
          </Alert>
        ) : devices.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t("deviceList.none")}
          </Alert>
        ) : (
          // Swipe left/right through the robots (arrows for mouse users)
          <DeviceCarousel
            itemWidth={{ xs: 210, sm: 230 }}
            arrowLabels={{
              previous: t("deviceList.previous"),
              next: t("deviceList.next"),
            }}
          >
            {devices
              .filter((device) => device.active === 1) // Only show currently active devices
              .map((device, index) => (
                <DeviceCard
                  key={device.device_id || device.pk_id || index}
                  device={device}
                  size={"medium"}
                  // Same minimal card for every role: picture, name, UUID, S/N
                  variant="control"
                  urlPrefix={"/device-controls/"}
                  buttonText={t("deviceList.controls")}
                  // Which robots a control user may operate is set per device
                  // in Controls Setup > Control Access, so the button is always on
                  disabled={false}
                />
              ))}
          </DeviceCarousel>
        )}
      </Container>
    </>
  );
}

export default DeviceListPage;
